import { dockTypes, rcTypes, slinkTechTypes } from '@/constants/deviceManager'
import useApp from '@/composables/useApp'
import type { Device, DeviceGroup } from '@/types/devices.types'

/**
 * Group a device list by deviceGroupID/deviceGroupName. Shared by the app
 * store's `groupedDevices` getter (project-scoped list) and views that group
 * an org-wide list (e.g. setup/deviceManagement).
 */
export function groupDevices(devices: Device[]): DeviceGroup[] {
  const groups: DeviceGroup[] = []
  devices.forEach((device) => {
    if (device.deviceGroupName && device.deviceGroupID) {
      let group = groups.find(
        (g) => g.groupName === device.deviceGroupName && g.groupId === device.deviceGroupID,
      )
      if (!group) {
        group = {
          groupName: device.deviceGroupName,
          devices: [],
          groupId: device.deviceGroupID,
        }
        groups.push(group)
      }
      group.devices.push(device)
    }
  })
  return groups
}

export const validatePin = (pin: string) => {
  const pinStr = pin.toString().trim()

  // Check if input is numeric
  if (!/^\d+$/.test(pinStr)) {
    return { valid: false, error: 'Pin must contain only digits.' }
  }

  // Check minimum length
  if (pinStr.length < 6) {
    return { valid: false, error: 'Pin must be at least 6 digits long.' }
  }

  // Forbidden sequences
  const forbiddenSequences = [
    '123456',
    '000000',
    '999999', // Original sequences
    '654321',
    '987654', // Descending sequences
    '111111',
    '222222',
    '333333',
    '444444',
    '555555',
    '666666',
    '777777',
    '888888', // Repeated digits
    '112233',
    '121212',
    '123123', // Repeating pairs
    '147258',
    '258369',
    '159357', // Keypad patterns
    '135790',
    '246810', // Arithmetic sequences
    '202020',
    '200000', // Year-like patterns
    '123321',
    '122221', // Palindromic sequences
  ]

  // Check for forbidden sequences
  for (const seq of forbiddenSequences) {
    if (pinStr.includes(seq)) {
      return { valid: false, error: `Pin cannot contain the sequence '${seq}'.` }
    }
  }

  // Check for repeated single digit (e.g., 111111, 222222)
  if (/^(\d)\1{5,}$/.test(pinStr)) {
    return { valid: false, error: 'Pin cannot consist of the same digit repeated.' }
  }

  // Check for repeating pairs (e.g., 121212, 123123)
  const pairPattern = /^(\d{2})\1{2,}$/
  const triplePattern = /^(\d{3})\1{1,}$/
  if (pairPattern.test(pinStr) || triplePattern.test(pinStr)) {
    return { valid: false, error: 'Pin cannot consist of repeating pairs or triples.' }
  }

  // If all checks pass
  return { valid: true, error: null }
}
export function findDevice(
  groupId: string | null,
  type: 'dock' | 'rc' | 'slinktech',
  returnObject: true,
): Device | null
export function findDevice(
  groupId: string | null,
  type: 'dock' | 'rc' | 'slinktech',
  returnObject?: false,
): string
export function findDevice(
  groupId: string | null,
  type: 'dock' | 'rc' | 'slinktech',
  returnObject = false,
): string | Device | null {
  const { organizationDevices } = useApp()
  const typeMap = {
    dock: dockTypes,
    rc: rcTypes,
    slinktech: slinkTechTypes,
  }
  const types = typeMap[type]

  // Guard against unexpected type values to prevent runtime crash
  if (!types) {
    return returnObject ? null : '0000'
  }

  const device = groupId
    ? organizationDevices.value.find(
        (d: any) => d.deviceGroupID === groupId && types.includes(d.deviceType),
      )
    : null

  if (returnObject) return device || null
  return device?.deviceID ?? '0000'
}

/**
 * Normalises the `/device-management/get-user-device-group` response into a
 * consistent `{ deviceGroupID, deviceGroupName? }[]` shape.
 * The backend currently returns the groups under `deviceGroupID` (array of
 * objects) or `deviceGroupIDs` (array of raw id strings); either (or a future
 * `deviceGroups` field) is accepted here.
 */
export function normalizeUserGroups(
  data: any,
): { deviceGroupID: string; deviceGroupName?: string }[] {
  if (!data) return []
  const source =
    (Array.isArray(data.deviceGroups) && data.deviceGroups) ||
    (Array.isArray(data.deviceGroupID) && data.deviceGroupID) ||
    (Array.isArray(data.deviceGroupIDs) && data.deviceGroupIDs) ||
    []

  return source
    .map((entry: any) => {
      if (typeof entry === 'string') return { deviceGroupID: entry }
      if (entry && typeof entry === 'object' && entry.deviceGroupID) {
        return {
          deviceGroupID: String(entry.deviceGroupID),
          deviceGroupName: entry.deviceGroupName,
        }
      }
      return null
    })
    .filter(
      (g: { deviceGroupID: string; deviceGroupName?: string } | null): g is {
        deviceGroupID: string
        deviceGroupName?: string
      } => !!g,
    )
}
